'use client';

import React, { useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';

import styles from './WordRevealStandalone.module.css';

/**
 * Word-by-word reveal that keeps the markup it is given.
 *
 * Two things set it apart from ParagraphReveal:
 *
 * 1. The children are rendered as-is and the words are split out of the DOM
 *    afterwards, so any content works — several paragraphs, <FormattedMessage>,
 *    inline links — instead of only a single paragraph of literal text. The text
 *    is therefore also visible before (and without) JavaScript.
 * 2. Scroll progress is measured from the live element rect on every frame, so
 *    the component neither registers a ScrollTrigger nor triggers a page-wide
 *    ScrollTrigger.refresh() that would disturb the other animations.
 */

const EDGE_FRACTIONS = { top: 0, center: 0.5, bottom: 1 };

const WORD_ATTRIBUTE = 'data-reveal-word';
const SPLIT_ATTRIBUTE = 'data-reveal-split';

const clamp01 = value => (value < 0 ? 0 : value > 1 ? 1 : value);

const toNumber = (value, fallback) => {
  const numeric = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const resolveEdge = (token, size) => {
  if (typeof token === 'number') return token;
  const value = String(token ?? '').trim();
  if (value in EDGE_FRACTIONS) return EDGE_FRACTIONS[value] * size;
  if (value.endsWith('%')) return (parseFloat(value) / 100) * size;
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

/**
 * Distance in pixels between the element edge and the viewport edge described
 * by a GSAP-style position string such as "top 80%". Zero when they meet.
 */
const edgeDistance = (spec, fallback, rect, viewportHeight) => {
  const [elementEdge = 'top', viewportEdge = 'top'] = String(spec || fallback)
    .trim()
    .split(/\s+/);
  return (
    rect.top + resolveEdge(elementEdge, rect.height) - resolveEdge(viewportEdge, viewportHeight)
  );
};

/**
 * The wrapper is display:contents so it stays out of the layout, which also
 * means it has no box of its own to measure. Fall back to the area its children
 * cover in that case.
 */
const measureRect = element => {
  const rect = element.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) return rect;

  const childRects = Array.from(element.children).map(child => child.getBoundingClientRect());
  if (childRects.length === 0) return rect;

  const top = Math.min(...childRects.map(child => child.top));
  const bottom = Math.max(...childRects.map(child => child.bottom));
  return { top, bottom, height: bottom - top };
};

/**
 * Wraps every word of every text node below `root` in its own span. Whitespace
 * stays a regular space so the browser keeps its line-break opportunities.
 */
const splitIntoWords = root => {
  if (root.hasAttribute(SPLIT_ATTRIBUTE)) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach(textNode => {
    const text = textNode.nodeValue;
    if (!text || !text.trim() || !textNode.parentNode) return;

    const fragment = document.createDocumentFragment();
    text.split(/(\s+)/).forEach(part => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        fragment.appendChild(document.createTextNode(' '));
        return;
      }
      const word = document.createElement('span');
      word.className = styles.word;
      word.setAttribute(WORD_ATTRIBUTE, '');
      word.textContent = part;
      fragment.appendChild(word);
    });

    textNode.parentNode.replaceChild(fragment, textNode);
  });

  root.setAttribute(SPLIT_ATTRIBUTE, '');
};

export default function WordRevealStandalone({
  children,
  className = '',
  animate = true,
  start = 'top 85%',
  end = 'top 60%',
  staggerEach = 0.04,
  wordDuration = 0.3,
  delay = 0,
  once = false,
  scrub = true,
  dimOpacity = 0.1,
}) {
  const containerRef = useRef(null);

  const stagger = toNumber(staggerEach, 0.04);
  const duration = toNumber(wordDuration, 0.3);
  const startDelay = toNumber(delay, 0);
  const dim = clamp01(toNumber(dimOpacity, 0.1));

  useLayoutEffect(() => {
    if (!animate) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    splitIntoWords(container);

    const words = Array.from(container.querySelectorAll(`[${WORD_ATTRIBUTE}]`));
    if (words.length === 0) return undefined;

    // Normalise the stagger timeline so scroll progress 0..1 covers every word.
    const totalDuration = startDelay + (words.length - 1) * stagger + duration;
    const windows = words.map((_, index) => {
      const from = (startDelay + index * stagger) / totalDuration;
      return { from, span: duration / totalDuration || 1 };
    });
    const rendered = words.map(() => -1);

    let frameId = null;
    let isSettled = false;
    let observer = null;

    const applyProgress = progress => {
      words.forEach((word, index) => {
        const { from, span } = windows[index];
        const local = clamp01((progress - from) / span);
        const eased = 1 - (1 - local) * (1 - local);
        const opacity = dim + (1 - dim) * eased;
        if (Math.abs(opacity - rendered[index]) < 0.005) return;
        rendered[index] = opacity;
        // Drop the inline style once revealed so the word ends up at a true
        // opacity 1 rather than at 0.999 of the eased curve.
        word.style.opacity = local >= 1 ? '' : String(opacity);
      });
    };

    const update = () => {
      if (isSettled) return;
      const rect = measureRect(container);
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const fromStart = edgeDistance(start, 'top 85%', rect, viewportHeight);
      const fromEnd = edgeDistance(end, 'top 60%', rect, viewportHeight);
      const travel = fromStart - fromEnd;
      const progress = travel === 0 ? (fromStart <= 0 ? 1 : 0) : clamp01(fromStart / travel);

      applyProgress(progress);

      if (once && progress >= 1) {
        isSettled = true;
        observer?.disconnect();
        observer = null;
      }
    };

    const tick = () => {
      update();
      if (isSettled) {
        frameId = null;
        return;
      }
      frameId = window.requestAnimationFrame(tick);
    };

    const startLoop = () => {
      if (isSettled) return;
      if (frameId == null) frameId = window.requestAnimationFrame(tick);
    };

    const stopLoop = () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      frameId = null;
    };

    update();

    // The loop runs by default and the observer is only allowed to pause it:
    // scrubbing must never depend on the observer having reported something.
    startLoop();

    // Only burn frames while the text is anywhere near the viewport. The
    // wrapper is display:contents and so has no box to intersect — watch the
    // children instead. First and last bracket the whole block, and the 100%
    // root margin leaves a viewport of slack on either side.
    const childElements = Array.from(container.children);
    const observedElements =
      childElements.length > 1
        ? [childElements[0], childElements[childElements.length - 1]]
        : childElements;

    if (typeof IntersectionObserver !== 'undefined' && observedElements.length > 0) {
      // A callback only carries the targets that changed, so the visible ones
      // are tracked across calls rather than read off the current batch.
      const visibleElements = new Set();

      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              visibleElements.add(entry.target);
            } else {
              visibleElements.delete(entry.target);
            }
          });

          update();

          if (visibleElements.size > 0) {
            startLoop();
          } else {
            stopLoop();
          }
        },
        { rootMargin: '100% 0px 100% 0px' }
      );
      observedElements.forEach(element => observer.observe(element));
    }

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      stopLoop();
      observer?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      words.forEach(word => {
        word.style.opacity = '';
      });
    };
  }, [animate, start, end, stagger, duration, startDelay, once, dim]);

  if (!animate) {
    return children;
  }

  return (
    <div ref={containerRef} className={classNames(styles.wordReveal, className)}>
      {children}
    </div>
  );
}
